import * as React from 'react';
import { Control, useController } from 'react-hook-form';
import { CmdError } from '@commander-services/gui-components';
import TooltipComponent from '../../TooltipComponent';
import SectionList from '../SectionList';
import { IGroupChipSelectOptions, IGroupChipSelectOption } from './interfaces';
import useOutsideClick from '../../../hooks/useOutsideClick';
import useKeyPress, { ESCAPE_KEY } from '../../../hooks/useKeyPress';
import { filterBinarySectionList } from '../../OnlineMapNew/Waypoints/WaypointService';

const usePrevious = (value: string) => {
  const ref: any = React.useRef();
  React.useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current ? JSON.parse(ref.current) : [];
};

interface ISelectedGroupChipItems {
  [groupName: string]: string[];
}

interface IGroupChipSelectProps {
  id: string;
  name: string;
  label: string;
  options: IGroupChipSelectOptions;
  control: Control<any>;
  searchPlaceholder: string;
  selectButton: string;
  selectButtonTooltip?: string;
  resetButton: string;
  resetButtonTooltip?: string;
  placeholder?: string;
  required?: boolean;
  onChangeCallback?: (number: number, values: string[]) => void;
  showDetail?: () => void;
  showPartialDetail?: (id: string) => void;
  itemsNumber?: number;
  selectedValues?: ISelectedGroupChipItems;
  isNewGroup?: boolean;
  searchTooltip?: string;
}

/*
!Nepouzivat tuto komponentu nikde, okrem pravidiel v alarmoch!!!!!!
*/

function GroupChipSelectHookForm(props: IGroupChipSelectProps): JSX.Element {
  // const listRef = React.createRef<List>();
  const initSelectedItems = (options: IGroupChipSelectOptions): ISelectedGroupChipItems => {
    const selectedItems: ISelectedGroupChipItems = {};
    Object.keys(options).forEach((groupName: string) => {
      selectedItems[groupName] = [];
    });
    return selectedItems;
  };

  const [isDropdownMenuOpen, setIsDropdownMenuOpen] = React.useState<boolean>(false);
  const [selectedItems, setSelectedItems] = React.useState<ISelectedGroupChipItems>(
    initSelectedItems(props.options)
  );
  const [searchString, setSearchString] = React.useState<string>('');
  const [optionsFromSearch, setOptionsFromSearch] = React.useState<IGroupChipSelectOptions>(
    props.options
  );

  const [newOptions, setNewOptions] = React.useState<IGroupChipSelectOptions>(props.options);
  const {
    field: { ref, onChange, onBlur, value },
    fieldState: { invalid, error },
  } = useController({
    name: props.name,
    control: props.control,
    defaultValue: '', // or the default value
  });

  const clickOutsideRef: React.RefObject<any> = React.useRef();

  useOutsideClick(clickOutsideRef, () => {
    setIsDropdownMenuOpen(false);
  });
  useKeyPress(ESCAPE_KEY, () => {
    setIsDropdownMenuOpen(false);
  });

  let errBorder = '';

  React.useEffect(() => {
    if (props.selectedValues) {
      setSelectedItems(props.selectedValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDropdownMenuOpen]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value: searchedValue } = event.currentTarget;
    setSearchString(searchedValue);
  };

  const handleFilter = React.useCallback(() => {
    const filteredGroups: IGroupChipSelectOptions = {};
    if (props.options) {
      if (props.isNewGroup) {
        Object.keys(newOptions).forEach((key: string) => {
          // const filtered = newOptions[key].filter((option: IGroupChipSelectOption) =>
          //   SearchService.removeAccent(option.item.toLowerCase()).includes(
          //     SearchService.removeAccent(searchString.toLowerCase())
          //   )
          // );

          const binarySort = newOptions[key].sort((a, b) => a.item.localeCompare(b.item));

          const filtered = filterBinarySectionList(searchString, binarySort);

          if (filtered.length > 0) {
            filteredGroups[key] = filtered;
          }
        });
      } else {
        Object.keys(props.options).forEach((key: string) => {
          // const filtered = props.options[key].filter((option: IGroupChipSelectOption) =>
          //   SearchService.removeAccent(option.item.toLowerCase()).includes(
          //     SearchService.removeAccent(value.toLowerCase())
          //   )
          // );
          const binarySort = newOptions[key].sort((a, b) => a.item.localeCompare(b.item));
          const filtered = filterBinarySectionList(searchString, binarySort);
          if (filtered.length > 0) {
            filteredGroups[key] = filtered;
          }
        });
      }

      if (searchString) {
        setOptionsFromSearch(filteredGroups);
      } else if (props.isNewGroup) {
        setOptionsFromSearch(newOptions);
      } else {
        setOptionsFromSearch(props.options);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchString,
    isDropdownMenuOpen,
    props.options,
    props.isNewGroup,
    newOptions,
    setOptionsFromSearch,
  ]);

  React.useEffect(() => {
    handleFilter();
  }, [handleFilter]);

  const handleDetail = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.preventDefault();
    event.stopPropagation();
    if (props.showDetail) {
      props.showDetail();
    } else {
      setIsDropdownMenuOpen(!isDropdownMenuOpen);
    }
  };

  const handlePartialDetail = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    const id = event.currentTarget.getAttribute('data-id');
    event.preventDefault();
    event.stopPropagation();
    if (props.showPartialDetail) {
      props.showPartialDetail(String(id));
    } else {
      setIsDropdownMenuOpen(!isDropdownMenuOpen);
    }
  };

  if (error) {
    errBorder = 'err-border';
  }

  const prevValue = usePrevious(JSON.stringify(value) || '');
  const prevOptions = usePrevious(JSON.stringify(props.options) || '');

  React.useEffect(() => {
    if (value && Object.keys(value).length > 0) {
      const initializedSelectedItems = initSelectedItems(props.options);
      Object.keys(value).forEach((groupName: string) => {
        initializedSelectedItems[groupName] = value[groupName];
      });
      setSelectedItems(initializedSelectedItems);
    } else {
      setSelectedItems(initSelectedItems(props.options));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // React.useEffect(() => {
  //   const isValue = value && Object.keys(value).length > 0;

  //   if (JSON.stringify(props.options) !== JSON.stringify(prevOptions)) {
  //     setOptionsFromSearch(props.options);
  //     if (!isValue) {
  //       setSelectedItems(initSelectedItems(props.options));
  //     }
  //   }

  //   if (isValue) {
  //     if (JSON.stringify(value) !== JSON.stringify(prevValue)) {
  //       const initializedSelectedItems = initSelectedItems(props.options);
  //       Object.keys(value).forEach((groupName: string) => {
  //         initializedSelectedItems[groupName] = value[groupName];
  //       });
  //       setSelectedItems(initializedSelectedItems);
  //     }
  //   }
  // });

  const handleShowDropdownMenu = (
    event:
      | React.MouseEvent<HTMLLabelElement, MouseEvent>
      | React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    event.preventDefault();
    setIsDropdownMenuOpen(!isDropdownMenuOpen);
  };

  const handleReset = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();

    if (props.isNewGroup) {
      const tempOptions: IGroupChipSelectOptions = {};

      if (Object.values(props.options)[0].length > 0) {
        Object.keys(props.options).forEach((items: any, index: number) => {
          if (index === 1) {
            tempOptions[items] = Object.values(props.options)[1];

            Object.values(props.options)[0].forEach((item: IGroupChipSelectOption) => {
              if (
                !tempOptions[items].find(
                  (comparedItems: IGroupChipSelectOption) =>
                    String(comparedItems.value) === String(item.value)
                )
              ) {
                tempOptions[items].push(item);
              }
            });
          } else {
            tempOptions[items] = [];
          }
        });

        setNewOptions(tempOptions);
        setSelectedItems(initSelectedItems(tempOptions));
        setOptionsFromSearch(tempOptions);
      } else {
        setNewOptions(props.options);
        setSelectedItems(initSelectedItems(props.options));
        setOptionsFromSearch(props.options);
      }
    } else {
      setSelectedItems(initSelectedItems(props.options));
    }
    setIsDropdownMenuOpen(false);
    onChange('');
  };

  const handleApply = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();

    if (props.isNewGroup) {
      const tempSelectedItems: ISelectedGroupChipItems = {};
      const tempOptions = {};

      if (
        Object.values(selectedItems)[0].length > 0 &&
        Object.values(selectedItems)[1].length > 0
      ) {
        Object.keys(selectedItems).forEach((items: any, index: number) => {
          if (index === 0) {
            tempSelectedItems[items] = Object.values(selectedItems)[0];
            Object.values(selectedItems)[1].forEach((itemsBoth: string) => {
              tempSelectedItems[items].push(itemsBoth);
            });
          } else {
            tempSelectedItems[items] = [];
          }
        });

        if (Object.values(props.options)[0].length > 0) {
          const tempOptionsApply: IGroupChipSelectOptions = {};

          Object.keys(props.options).forEach((items: any, index: number) => {
            if (index === 1) {
              tempOptionsApply[items] = Object.values(props.options)[1];

              Object.values(props.options)[0].forEach((item: IGroupChipSelectOption) => {
                if (
                  Object.values(tempSelectedItems)[0].includes(String(item.value)) &&
                  !tempOptionsApply[items].find(
                    (comparedItems: IGroupChipSelectOption) =>
                      String(comparedItems.value) === String(item.value)
                  )
                ) {
                  tempOptionsApply[items].push(item);
                }
              });
            } else {
              tempOptionsApply[items] = [];
            }
          });

          Object.keys(tempOptionsApply).forEach((items: any, index: number) => {
            if (index === 0) {
              tempOptions[items] = Object.values(tempOptionsApply)[1].filter(
                (selectedGroup: IGroupChipSelectOption) =>
                  Object.values(tempSelectedItems)[0].includes(String(selectedGroup.value))
              );
            } else {
              tempOptions[items] = Object.values(props.options)[1].filter(
                (selectedGroup: IGroupChipSelectOption) =>
                  !Object.values(tempSelectedItems)[0].includes(String(selectedGroup.value))
              );
            }
          });
        } else {
          Object.keys(props.options).forEach((items: any, index: number) => {
            if (index === 0) {
              tempOptions[items] = Object.values(props.options)[1].filter(
                (selectedGroup: IGroupChipSelectOption) =>
                  Object.values(tempSelectedItems)[0].includes(String(selectedGroup.value))
              );
            } else {
              tempOptions[items] = Object.values(props.options)[1].filter(
                (selectedGroup: IGroupChipSelectOption) =>
                  !Object.values(tempSelectedItems)[0].includes(String(selectedGroup.value))
              );
            }
          });
        }

        setNewOptions(tempOptions);

        setOptionsFromSearch(tempOptions);

        setSelectedItems(tempSelectedItems);
        onChange(tempSelectedItems);
        setIsDropdownMenuOpen(false);
      } else if (Object.values(selectedItems)[1].length > 0) {
        Object.keys(selectedItems).forEach((items: any, index: number) => {
          if (index === 0) {
            tempSelectedItems[items] = Object.values(selectedItems)[1];
          } else {
            tempSelectedItems[items] = [];
          }
        });

        if (Object.values(props.options)[0].length > 0) {
          const tempOptionsApply: IGroupChipSelectOptions = {};

          Object.keys(props.options).forEach((items: any, index: number) => {
            if (index === 1) {
              tempOptionsApply[items] = Object.values(props.options)[1];
            } else {
              tempOptionsApply[items] = [];
            }
          });

          Object.keys(tempOptionsApply).forEach((items: any, index: number) => {
            if (index === 0) {
              tempOptions[items] = Object.values(tempOptionsApply)[1].filter(
                (selectedGroup: IGroupChipSelectOption) =>
                  Object.values(tempSelectedItems)[0].includes(String(selectedGroup.value))
              );
            } else {
              tempOptions[items] = Object.values(props.options)[1].filter(
                (selectedGroup: IGroupChipSelectOption) =>
                  !Object.values(tempSelectedItems)[0].includes(String(selectedGroup.value))
              );
            }
          });
        } else {
          Object.keys(props.options).forEach((items: any, index: number) => {
            if (index === 0) {
              tempOptions[items] = Object.values(props.options)[1].filter(
                (selectedGroup: IGroupChipSelectOption) =>
                  Object.values(tempSelectedItems)[0].includes(String(selectedGroup.value))
              );
            } else {
              tempOptions[items] = Object.values(props.options)[1].filter(
                (selectedGroup: IGroupChipSelectOption) =>
                  !Object.values(tempSelectedItems)[0].includes(String(selectedGroup.value))
              );
            }
          });
        }

        setNewOptions(tempOptions);

        setOptionsFromSearch(tempOptions);

        setSelectedItems(tempSelectedItems);
        onChange(tempSelectedItems);
        setIsDropdownMenuOpen(false);
      } else if (Object.values(selectedItems)[0].length > 0) {
        Object.keys(selectedItems).forEach((items: any, index: number) => {
          if (index === 0) {
            tempSelectedItems[items] = Object.values(selectedItems)[0];
          } else {
            tempSelectedItems[items] = [];
          }
        });

        if (Object.values(props.options)[0].length > 0) {
          const tempOptionsApply: IGroupChipSelectOptions = {};

          Object.keys(props.options).forEach((items: any, index: number) => {
            if (index === 1) {
              tempOptionsApply[items] = Object.values(props.options)[1];

              Object.values(props.options)[0].forEach((item: IGroupChipSelectOption) => {
                if (
                  Object.values(tempSelectedItems)[0].includes(String(item.value)) &&
                  !tempOptionsApply[items].find(
                    (comparedItems: IGroupChipSelectOption) =>
                      String(comparedItems.value) === String(item.value)
                  )
                ) {
                  tempOptionsApply[items].push(item);
                }
              });
            } else {
              tempOptionsApply[items] = [];
            }
          });

          Object.keys(tempOptionsApply).forEach((items: any, index: number) => {
            if (index === 0) {
              tempOptions[items] = Object.values(tempOptionsApply)[1].filter(
                (selectedGroup: IGroupChipSelectOption) =>
                  Object.values(tempSelectedItems)[0].includes(String(selectedGroup.value))
              );
            } else {
              tempOptions[items] = Object.values(props.options)[1].filter(
                (selectedGroup: IGroupChipSelectOption) =>
                  !Object.values(tempSelectedItems)[0].includes(String(selectedGroup.value))
              );
            }
          });
        } else {
          Object.keys(props.options).forEach((items: any, index: number) => {
            if (index === 0) {
              tempOptions[items] = Object.values(props.options)[1].filter(
                (selectedGroup: IGroupChipSelectOption) =>
                  Object.values(tempSelectedItems)[0].includes(String(selectedGroup.value))
              );
            } else {
              tempOptions[items] = Object.values(props.options)[1].filter(
                (selectedGroup: IGroupChipSelectOption) =>
                  !Object.values(tempSelectedItems)[0].includes(String(selectedGroup.value))
              );
            }
          });
        }

        setOptionsFromSearch(tempOptions);
        setNewOptions(tempOptions);

        setSelectedItems(tempSelectedItems);
        onChange(tempSelectedItems);
        setIsDropdownMenuOpen(false);
      } else if (
        Object.values(selectedItems)[0].length === 0 &&
        Object.values(selectedItems)[1].length === 0
      ) {
        if (Object.values(props.options)[0].length > 0) {
          Object.keys(props.options).forEach((items: any, index: number) => {
            if (index === 1) {
              tempOptions[items] = Object.values(props.options)[1];

              Object.values(props.options)[0].forEach((item: IGroupChipSelectOption) => {
                if (
                  !tempOptions[items].find(
                    (comparedItems: IGroupChipSelectOption) =>
                      String(comparedItems.value) === String(item.value)
                  )
                ) {
                  tempOptions[items].push(item);
                }
              });
            } else {
              tempOptions[items] = [];
            }
          });

          setNewOptions(tempOptions);
          setOptionsFromSearch(tempOptions);
          onChange('');
        } else {
          setNewOptions(props.options);
          setSelectedItems(initSelectedItems(props.options));
          setOptionsFromSearch(props.options);
          onChange('');
        }
      } else {
        setNewOptions(props.options);

        setOptionsFromSearch(props.options);

        setSelectedItems(selectedItems);
        onChange(selectedItems);
        setIsDropdownMenuOpen(false);
      }
    } else {
      onChange(selectedItems);
      setIsDropdownMenuOpen(false);
    }

    if (props.onChangeCallback) {
      let checkedItemsNumber = 0;
      let values: string[] = [];
      Object.values(selectedItems).forEach((item: string[]) => {
        checkedItemsNumber += item.length;
        values = values.concat(item);
      });
      props.onChangeCallback(checkedItemsNumber, values);
    }
  };

  const handleSelectItem = (
    event: React.MouseEvent<HTMLLabelElement, MouseEvent> | React.ChangeEvent<HTMLInputElement>
  ) => {
    event.preventDefault();
    const dataValue = event.currentTarget.getAttribute('data-value');
    const groupName = event.currentTarget.getAttribute('data-group');
    const disabled = event.currentTarget.getAttribute('data-disabled');

    if (disabled === 'true') {
      return;
    }

    if (!groupName) {
      return;
    }

    if (dataValue) {
      const groupSelectedItems = selectedItems[groupName];

      if (!groupSelectedItems.includes(dataValue)) {
        groupSelectedItems.push(dataValue);

        const newSelectedItems: ISelectedGroupChipItems = {
          ...selectedItems,
          [groupName]: groupSelectedItems,
        };
        setSelectedItems(newSelectedItems);
      } else {
        const newSelectedItems: ISelectedGroupChipItems = {
          ...selectedItems,
          [groupName]: groupSelectedItems.filter((item: string) => item !== dataValue),
        };
        setSelectedItems(newSelectedItems);
      }
    }
  };

  const handleRemoveItem = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    event.stopPropagation();
    const selectedValue = event.currentTarget.getAttribute('data-value');
    const groupName = event.currentTarget.getAttribute('data-group');

    if (selectedValue && groupName) {
      const newSelectedItems: ISelectedGroupChipItems = {
        ...selectedItems,
        [groupName]: selectedItems[groupName].filter((item: string) => item !== selectedValue),
      };
      let countItems = 0;
      Object.keys(newSelectedItems).forEach((group: string) => {
        countItems += newSelectedItems[group].length;
      });
      if (countItems === 0) {
        // setFieldTouched(props.name);
        setSelectedItems(newSelectedItems);
        onChange('');
      } else {
        setSelectedItems(newSelectedItems);
        onChange(newSelectedItems);
      }
    }
  };

  const getSelectedFieldValues = (fieldValue, options: IGroupChipSelectOptions) => {
    const selectedFieldValueItems: IGroupChipSelectOption[] = [];
    if (fieldValue) {
      Object.keys(fieldValue).forEach((groupName: string) => {
        if (fieldValue[groupName].length > 0 && options && options[groupName]) {
          options[groupName]
            .filter((optionItem: IGroupChipSelectOption) =>
              fieldValue[groupName].includes(String(optionItem.value))
            )
            .forEach((optionItem: IGroupChipSelectOption) => {
              const newOptionItem = {
                ...optionItem,
                groupName,
              };
              selectedFieldValueItems.push(newOptionItem);
            });
        }
      });
      return selectedFieldValueItems;
    }
    return [];
  };
  let selectedFieldValueItems: IGroupChipSelectOption[] = [];

  if (props.isNewGroup) {
    selectedFieldValueItems = getSelectedFieldValues(value, newOptions);
  } else {
    selectedFieldValueItems = getSelectedFieldValues(value, props.options);
  }

  const sections = [
    {
      title: Object.keys(optionsFromSearch)[0] ? Object.keys(optionsFromSearch)[0] : '',
      data: Object.values(optionsFromSearch)[0] ? [...Object.values(optionsFromSearch)[0]] : [],
    },
    {
      title: Object.keys(optionsFromSearch)[1] ? Object.keys(optionsFromSearch)[1] : '',
      data: Object.values(optionsFromSearch)[1] ? [...Object.values(optionsFromSearch)[1]] : [],
    },
  ];

  const renderHeader = ({ title, sectionIndex, key, style, isScrolling, isVisible, parent }) => {
    return (
      <div key={key} style={style}>
        <div
          id={`title-${props.id}`}
          className="b-dropdown__title"
          data-cy={`groupChipSelect-header-${props.id}`}
        >
          {title}
        </div>
      </div>
    );
  };

  return (
    <div
      ref={clickOutsideRef}
      className={`group-chip-select-component form-group ${error && 'form-group-error'}`}
    >
      <div>
        <label className="d-block form-label" onClick={handleShowDropdownMenu}>
          {props.required && <span className="text-danger required-field-info">*</span>}
          {props.label}
        </label>
        <div className="d-block">
          <div id={props.id} className={`b-dropdown ${isDropdownMenuOpen ? 'open' : ''}`}>
            <div
              id={`choose-${props.id}`}
              className={`form-control-labels ${errBorder}`}
              onClick={handleShowDropdownMenu}
              data-cy="groupChipSelect-choose"
            >
              {selectedFieldValueItems.length > 0 ? (
                <>
                  {(selectedFieldValueItems.length > 0 ||
                    selectedFieldValueItems.length < (props.itemsNumber || 2)) &&
                    selectedFieldValueItems
                      .filter(
                        (_optionItem: IGroupChipSelectOption, index: number) =>
                          index < (props.itemsNumber || 2)
                      )
                      .map((optionItem: IGroupChipSelectOption) =>
                        optionItem.popup ? (
                          <TooltipComponent
                            id={props.id}
                            firstItems={true}
                            positionLeft={true}
                            overlay={<div style={{ color: '#fff' }}>{optionItem.popup}</div>}
                          >
                            <div
                              onClick={handlePartialDetail}
                              data-id={optionItem.value}
                              className={`label ${
                                optionItem.passed === false ? 'label-primary' : ''
                              }`}
                              key={`label-${optionItem.item}`}
                              data-cy={`groupchips-${optionItem.value}`}
                            >
                              {optionItem.passed === false && (
                                <span>
                                  <i
                                    id={`icon-${optionItem.item}`}
                                    className="cmd-icon cmd-icon-exclamation"
                                  />
                                  &nbsp;&nbsp;&nbsp;
                                </span>
                              )}
                              {`${optionItem.item} `}
                              <a
                                className="label-action cmd-icon cmd-icon-remove-sm"
                                data-value={optionItem.value}
                                data-group={optionItem.groupName}
                                data-cy="remove-icon"
                                onClick={handleRemoveItem}
                              />
                            </div>
                          </TooltipComponent>
                        ) : (
                          <div
                            onClick={handlePartialDetail}
                            data-id={optionItem.value}
                            className={`label ${
                              optionItem.passed === false ? 'label-primary' : ''
                            }`}
                            key={`label-${optionItem.item}`}
                            data-cy={`groupchips-${optionItem.value}`}
                          >
                            {optionItem.passed === false && (
                              <span>
                                <i
                                  id={`icon-${optionItem.item}`}
                                  className="cmd-icon cmd-icon-exclamation text-white"
                                />
                                &nbsp;&nbsp;&nbsp;
                              </span>
                            )}
                            {`${optionItem.item} `}
                            <a
                              className="label-action cmd-icon cmd-icon-remove-sm"
                              data-value={optionItem.value}
                              data-group={optionItem.groupName}
                              data-cy="remove-icon"
                              onClick={handleRemoveItem}
                            />
                          </div>
                        )
                      )}
                  {selectedFieldValueItems.length > (props.itemsNumber || 2) && (
                    <div onClick={handleDetail} className="label">
                      +{selectedFieldValueItems.length - (props.itemsNumber || 2)}
                    </div>
                  )}
                </>
              ) : (
                <span>{props.placeholder}</span>
              )}
            </div>
            {isDropdownMenuOpen && (
              <div className="b-dropdown__content">
                <div id={`group-chip-select-${props.id}`} className="b-dropdown__header">
                  <input
                    type="text"
                    className="form-control"
                    onChange={handleSearch}
                    value={searchString}
                    placeholder={props.searchPlaceholder}
                    autoFocus={true}
                    data-cy={`group-chip-select-search-${props.id}`}
                    title={props.searchTooltip || ''}
                  />
                </div>

                <div style={{ maxHeight: '200px' }}>
                  <SectionList
                    // ref={listRef}
                    sections={sections}
                    sectionHeaderRenderer={renderHeader}
                    sectionHeaderHeight={36}
                    itemData={optionsFromSearch}
                    itemSize={36}
                    // width={363}
                    height={
                      200
                      // Object.values(optionsFromSearch)[indexHeader].length > 6
                      //   ? 200
                      //   : 36 * Object.values(optionsFromSearch)[indexHeader].length
                    }
                    rowRenderer={({
                      item,
                      sectionIndex,
                      rowIndex,
                      key,
                      style,
                      isScrolling,
                      isVisible,
                      parent,
                    }) => {
                      return (
                        <ul
                          key={key}
                          style={style}
                          id={`multipleCheckboxes-${props.id}`}
                          className="b-dropdown__list"
                        >
                          <li>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                              }}
                            >
                              <div className="e-checkbox" style={{ flex: '1' }}>
                                <label
                                  onClick={handleSelectItem}
                                  id={`${props.id}_selectCheckbox`}
                                  data-cy={`group-select-multiple-label-${
                                    Object.values(optionsFromSearch)[sectionIndex][rowIndex].value
                                  }`}
                                  data-value={
                                    Object.values(optionsFromSearch)[sectionIndex][rowIndex].value
                                  }
                                  data-group={Object.keys(optionsFromSearch)[sectionIndex]}
                                  data-disabled={
                                    Object.values(optionsFromSearch)[sectionIndex][rowIndex]
                                      .disabled
                                  }
                                >
                                  <input
                                    type="checkbox"
                                    value={
                                      Object.values(optionsFromSearch)[sectionIndex][rowIndex].value
                                    }
                                    disabled={
                                      Object.values(optionsFromSearch)[sectionIndex][rowIndex]
                                        .disabled || false
                                    }
                                    checked={
                                      selectedItems[Object.keys(optionsFromSearch)[sectionIndex]]
                                        ? selectedItems[
                                            Object.keys(optionsFromSearch)[sectionIndex]
                                          ].includes(
                                            String(
                                              Object.values(optionsFromSearch)[sectionIndex][
                                                rowIndex
                                              ].value
                                            )
                                          )
                                        : false
                                    }
                                    data-value={
                                      Object.values(optionsFromSearch)[sectionIndex][rowIndex].value
                                    }
                                    data-key={Object.keys(optionsFromSearch)[sectionIndex]}
                                    onChange={handleSelectItem}
                                    data-cy={`checkbox-${
                                      Object.values(optionsFromSearch)[sectionIndex][rowIndex].value
                                    }`}
                                  />
                                  <span id={props.id}>
                                    {Object.values(optionsFromSearch)[sectionIndex][rowIndex].item}
                                  </span>
                                </label>
                              </div>{' '}
                              <div style={{ justifyContent: 'flex-end' }}>
                                {Object.values(optionsFromSearch)[sectionIndex][rowIndex].type ===
                                  'private' && (
                                  <i
                                    className="cmd-icon cmd-icon-eye-hide"
                                    style={{ marginRight: '4px' }}
                                  />
                                )}
                                {Object.values(optionsFromSearch)[sectionIndex][rowIndex]
                                  .customerLabel &&
                                  Object.values(optionsFromSearch)[sectionIndex][rowIndex]
                                    .customerLabel}
                              </div>
                              {Object.values(optionsFromSearch)[sectionIndex][rowIndex].passed ===
                                false && (
                                <i
                                  id={`icon-${props.id}`}
                                  className="cmd-icon cmd-icon-exclamation text-primary"
                                  style={{ justifyContent: 'flex-end' }}
                                />
                              )}
                            </div>
                          </li>
                        </ul>
                      );
                    }}
                    rowHeight={36}
                  />
                </div>

                <div id={`footer-${props.id}`} className="b-dropdown__footer">
                  <a
                    id={`reset-${props.id}`}
                    data-cy={`reset-${props.id}`}
                    href="#"
                    className="e-button"
                    onClick={handleReset}
                    title={props.resetButtonTooltip || ''}
                  >
                    {props.resetButton}
                  </a>
                  <a
                    id={`apply-${props.id}`}
                    data-cy={`apply-${props.id}`}
                    href="#"
                    className="e-button e-button--gray"
                    onClick={handleApply}
                    title={props.selectButtonTooltip || ''}
                  >
                    {props.selectButton}
                  </a>
                </div>
              </div>
            )}
          </div>
          {error && <CmdError message={error.message} />}
        </div>
      </div>
    </div>
  );
}

export default GroupChipSelectHookForm;
